import { useState, useRef } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, Database } from "lucide-react";
import { toast } from "sonner";

// Dialog phases — keeps the wizard logic readable.
const PHASE_PICK = "pick";
const PHASE_PREVIEW = "preview";
const PHASE_DONE = "done";

export default function BrandCatalogImportDialog() {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState(PHASE_PICK);
  const [file, setFile] = useState(null);
  const [brand, setBrand] = useState("");
  const [slugPrefix, setSlugPrefix] = useState("");
  const [lenient, setLenient] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setPhase(PHASE_PICK);
    setFile(null);
    setBrand("");
    setSlugPrefix("");
    setLenient(false);
    setPreview(null);
    setCommitted(null);
  };

  const onOpenChange = (next) => {
    if (!next) reset();
    setOpen(next);
  };

  const submitPreview = async () => {
    if (!file) return toast.error("Pick a .xlsx file first");
    if (!brand.trim()) return toast.error("Brand name is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("brand", brand.trim());
      if (slugPrefix.trim()) fd.append("slug_prefix", slugPrefix.trim());
      fd.append("dry_run", "true");
      fd.append("lenient", lenient ? "true" : "false");
      const { data } = await api.post("/admin/import-catalog", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setPhase(PHASE_PREVIEW);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't parse the file");
    } finally {
      setBusy(false);
    }
  };

  const submitCommit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("brand", brand.trim());
      if (slugPrefix.trim()) fd.append("slug_prefix", slugPrefix.trim());
      fd.append("dry_run", "false");
      fd.append("lenient", lenient ? "true" : "false");
      const { data } = await api.post("/admin/import-catalog", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCommitted(data);
      setPhase(PHASE_DONE);
      toast.success(`Imported ${data.inserted} new + ${data.updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          data-testid="open-brand-import-btn"
          variant="outline"
          size="sm"
          className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-amber-500/40"
        >
          <Database className="w-3.5 h-3.5 mr-1.5" /> Import Brand Catalog
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="brand-import-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-zinc-100 flex items-center gap-2">
            <Database className="w-5 h-5 text-amber-300" /> Import Brand Catalog
          </DialogTitle>
        </DialogHeader>

        {phase === PHASE_PICK && (
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider mb-1 block">Workbook (.xlsx)</Label>
              <input
                ref={fileInputRef}
                data-testid="brand-import-file-input"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                data-testid="brand-import-pick-file-btn"
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-zinc-900 border-dashed border-zinc-700 hover:border-amber-500/60 text-zinc-300 hover:text-zinc-100"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {file ? file.name : "Pick a .xlsx (Master Catalog sheet)"}
              </Button>
            </div>

            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider mb-1 block">Canonical brand name</Label>
              <Input
                data-testid="brand-import-brand-input"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder='e.g. "Mini GT", "Tarmac Works"'
                className="bg-zinc-900 border-zinc-700 text-zinc-100"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Match an existing brand profile so tier + MSRP auto-resolve. New brands are accepted but won't get tier styling on the public Brand Index.
              </p>
            </div>

            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider mb-1 block">
                Slug prefix <span className="opacity-60 normal-case">(optional)</span>
              </Label>
              <Input
                data-testid="brand-import-slug-input"
                value={slugPrefix}
                onChange={(e) => setSlugPrefix(e.target.value)}
                placeholder='Defaults to a slug of the brand name (e.g. "mini-gt")'
                className="bg-zinc-900 border-zinc-700 text-zinc-100"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Used to namespace release_ids. Only override if you've previously imported this brand under a different prefix.
              </p>
            </div>

            <label
              htmlFor="brand-import-lenient"
              className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 cursor-pointer hover:border-amber-500/40 transition"
            >
              <input
                id="brand-import-lenient"
                data-testid="brand-import-lenient-toggle"
                type="checkbox"
                checked={lenient}
                onChange={(e) => setLenient(e.target.checked)}
                className="mt-0.5 accent-amber-500"
              />
              <div>
                <p className="text-sm font-semibold text-zinc-200">Include unverified rows as stubs</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                  Rows where <code className="text-zinc-400">Casting / Vehicle</code> is blank will import as placeholder entries
                  with <code className="text-zinc-400">status = "Needs backfill"</code>. Series #, Toy #, and Line are kept —
                  you can fill in the car name later via Edit Release.
                </p>
              </div>
            </label>

            <Button
              data-testid="brand-import-preview-btn"
              onClick={submitPreview}
              disabled={busy || !file || !brand.trim()}
              className="w-full bg-amber-600 hover:bg-amber-500 text-zinc-950 font-semibold"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4 mr-2" /> Preview import</>}
            </Button>
          </div>
        )}

        {phase === PHASE_PREVIEW && preview && (
          <div className="space-y-4 mt-2" data-testid="brand-import-preview-panel">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Total rows" value={preview.summary?.total_rows ?? 0} />
              <Stat label="Skipped (unverified)" value={preview.skipped_rows} accent={preview.skipped_rows > 0 ? "text-zinc-400" : "text-emerald-300"} />
              <Stat label="Will INSERT" value={preview.would_insert ?? 0} accent="text-emerald-300" />
              <Stat label="Will UPDATE" value={preview.would_update ?? 0} accent="text-amber-300" />
              {preview.stub_rows > 0 && (
                <Stat label="Stubs (backfill later)" value={preview.stub_rows} accent="text-fuchsia-300" />
              )}
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-xs">
              <p className="text-zinc-500 mb-2 uppercase tracking-wider">Brand resolution</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-zinc-300">
                <span className="text-zinc-500">Canonical brand</span>
                <span className="font-mono">{preview.summary?.brand}</span>
                <span className="text-zinc-500">Tier</span>
                <span className="font-mono">{preview.summary?.tier}</span>
                <span className="text-zinc-500">MSRP default</span>
                <span className="font-mono">{preview.summary?.msrp_default ?? "—"}</span>
                <span className="text-zinc-500">Slug prefix</span>
                <span className="font-mono">{preview.summary?.slug_prefix}</span>
              </div>
            </div>

            {preview.preview?.length > 0 && (
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                <p className="text-zinc-500 text-[11px] uppercase tracking-wider mb-2">First 5 rows</p>
                <ul className="text-xs space-y-1.5">
                  {preview.preview.map((d) => (
                    <li key={d.release_id} className="flex items-baseline gap-2">
                      <span className="font-mono text-zinc-500 text-[10px] w-14 shrink-0">{d.khmg_number}</span>
                      <span className="text-zinc-200">{d.release_name}</span>
                      {d.is_chase && <span className="text-amber-300 text-[10px] font-bold">CHASE</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                data-testid="brand-import-back-btn"
                variant="outline"
                onClick={() => setPhase(PHASE_PICK)}
                className="bg-zinc-900 border-zinc-700"
                disabled={busy}
              >
                Back
              </Button>
              <Button
                data-testid="brand-import-confirm-btn"
                onClick={submitCommit}
                disabled={busy || (preview.would_insert + preview.would_update) === 0}
                className="flex-1 bg-rose-600 hover:bg-rose-500 text-white"
              >
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirm import</>}
              </Button>
            </div>
          </div>
        )}

        {phase === PHASE_DONE && committed && (
          <div className="text-center py-8 space-y-3" data-testid="brand-import-done-panel">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <p className="text-zinc-100 font-semibold text-lg">Import complete</p>
            <div className="text-sm text-zinc-400 space-y-0.5">
              <p><span className="text-emerald-300 font-mono">{committed.inserted}</span> new releases inserted</p>
              <p><span className="text-amber-300 font-mono">{committed.updated}</span> existing releases updated</p>
              {committed.skipped_rows > 0 && (
                <p><span className="text-zinc-500 font-mono">{committed.skipped_rows}</span> rows skipped (placeholder / unverified)</p>
              )}
            </div>
            <div className="flex gap-2 justify-center pt-2">
              <Button
                onClick={reset}
                variant="outline"
                className="bg-zinc-900 border-zinc-700"
                data-testid="brand-import-another-btn"
              >
                Import another
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="brand-import-close-btn"
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {phase === PHASE_PICK && (
          <p className="text-[11px] text-zinc-600 mt-3 leading-relaxed">
            <AlertCircle className="w-3 h-3 inline -mt-0.5 mr-1" />
            Workbook must contain a <code className="text-zinc-400">Master Catalog</code> sheet with the standard 14 columns
            (Catalog ID, Year, Line, Series/Subline, Series #, Toy #, Casting / Vehicle, Color / Livery, Chase, Package Type, Case/Mix, Research Status, Source URL, Notes).
            See the catalog-prompt section of <code className="text-zinc-400">/app/memory/PRD.md</code>.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent = "text-zinc-100" }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5">
      <p className={`text-2xl font-mono font-bold ${accent}`}>{(value || 0).toLocaleString()}</p>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
    </div>
  );
}
